import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Modal, Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Banknote,
  Trash2,
  Pencil,
  FileText,
  Receipt,
  Info,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Archive,
  TrendingDown,
  Scissors,
  ArrowDownCircle,
  ArrowUpCircle,
  SlidersHorizontal,
  Shuffle,
  Flag,
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import VerificationDetailsModal from '../Verification/VerificationDetailsModal';
import DisposalModal from './DisposalModal';
import WritedownModal from './WritedownModal';
import {
  ASSET_FORM_STYLES,
  CategoryChip,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_META,
  EVENT_TONE_STYLES,
  StatusPill,
  addMonths,
  dateToPeriod,
  formatDate,
  formatMonthYear,
  formatNumber,
  getAssetAccountingUnavailable,
  getCategoryMeta,
  periodToDate,
  round2,
  unwrapData,
} from './assetUtils';

/**
 * Balance-decline chart — ApexCharts AREA with a simple straight line
 * (stepline was tried and rejected: 60 tiny monthly steps read as noise).
 * The area under the curve is filled, booked part stronger and the
 * projected remainder fainter, split at the Idag annotation. X-axis shows
 * short month names, with the year shown at each January.
 */
const BalanceDeclineChart = ({ points }) => {
  const config = useMemo(() => {
    if (!points || points.length < 2) return null;
    // One point per month (keep the month's LAST value — the closing book
    // value): the acquisition and the first depreciation share a month and
    // would otherwise create a zero-width glitch step.
    const byMonth = new Map();
    points.forEach((p) => {
      const key = `${p.date.getFullYear()}-${p.date.getMonth()}`;
      byMonth.set(key, p);
    });
    const monthly = [...byMonth.values()];
    if (monthly.length < 2) return null;
    const data = monthly.map((p) => [p.date.getTime(), round2(p.value)]);
    const projectedCount = monthly.filter((p) => p.projected).length;
    const bookedCount = monthly.length - projectedCount;

    return {
      series: [{ name: 'Bokf\u00f6rt v\u00e4rde', data }],
      bookedCount,
      options: {
        chart: {
          type: 'area',
          height: 340,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
          parentHeightOffset: 0,
        },
        colors: ['#44814E'],
        stroke: { curve: 'straight', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 0, opacityFrom: 0.45, opacityTo: 0.08 },
        },
        // The not-yet-posted tail: SAME continuous solid line (dashes shred a
        // fine-stepped staircase into noise) — the future reads from the
        // fainter fill + the Idag boundary line instead.
        forecastDataPoints: { count: projectedCount, dashArray: 0, fillOpacity: 0.35 },
        annotations: bookedCount > 0 && projectedCount > 0 ? {
          xaxis: [{
            x: data[bookedCount - 1][0],
            borderColor: '#94a3b8',
            strokeDashArray: 3,
            label: {
              text: 'Idag',
              orientation: 'horizontal',
              position: 'top',
              style: { color: '#64748b', background: '#fff', fontSize: '10px' },
            },
          }],
        } : {},
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 4 } },
        grid: {
          borderColor: '#f1f5f9',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 8, right: 8 },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            // Month short names; at January (and nowhere else) the year.
            formatter: (value, timestamp) => {
              const d = new Date(timestamp);
              if (Number.isNaN(d.getTime())) return '';
              if (d.getMonth() === 0) return String(d.getFullYear());
              return d.toLocaleDateString('sv-SE', { month: 'short' }).replace('.', '');
            },
            style: { colors: '#94a3b8', fontSize: '11px' },
            hideOverlappingLabels: true,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
          crosshairs: { show: true, stroke: { color: '#cbd5e1', width: 1, dashArray: 0 } },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: (value) => formatNumber(value, 0),
            style: { colors: '#94a3b8', fontSize: '11px' },
          },
        },
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const ts = w.globals.seriesX[seriesIndex][dataPointIndex];
            const value = series[seriesIndex][dataPointIndex];
            const d = new Date(ts);
            const label = d.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
            const state = dataPointIndex >= bookedCount ? 'Kommande' : 'Bokf\u00f6rt';
            return '<div style="padding:6px 10px;font-size:12px;">'
              + '<div style="font-weight:600;color:#111827;">' + label + '</div>'
              + '<div style="color:#374151;font-variant-numeric:tabular-nums;">' + formatNumber(value) + ' kr</div>'
              + '<div style="color:#94a3b8;">' + state + '</div>'
              + '</div>';
          },
        },
        legend: { show: false },
      },
    };
  }, [points]);

  if (!config) return null;

  return (
    <div>
      {/* Taller plot improves the staircase's tread:riser proportion; width
          stays the card's full content width so the chart aligns with the
          summary strip and tables. */}
      <ReactApexChart options={config.options} series={config.series} type="area" height={340} />
      <div className="d-flex align-items-center gap-3" style={{ fontSize: '0.75rem', color: 'var(--secondary)', marginTop: -8 }}>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(68,129,78,0.45)', border: '1px solid #44814E', display: 'inline-block' }} />
          Avskrivet
        </span>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(68,129,78,0.16)', border: '1px solid rgba(68,129,78,0.5)', display: 'inline-block' }} />
          Kommande
        </span>
      </div>
    </div>
  );
};

// Digits-style event chip: tinted rounded square + line icon per event type.
const EVENT_ICONS = {
  ShoppingCart, Archive, TrendingDown, Banknote, Trash2, Scissors,
  ArrowDownCircle, ArrowUpCircle, SlidersHorizontal, Pencil, Shuffle,
};

const EventChip = ({ eventType, hasVerification }) => {
  const meta = EVENT_TYPE_META[eventType] || { icon: 'SlidersHorizontal', tone: 'neutral' };
  const Icon = EVENT_ICONS[meta.icon] || SlidersHorizontal;
  const tone = EVENT_TONE_STYLES[meta.tone] || EVENT_TONE_STYLES.neutral;
  return (
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: 9,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        opacity: hasVerification === false ? 0.7 : 1,
        ...tone,
      }}
      aria-hidden="true"
    >
      <Icon size={15} strokeWidth={1.75} />
    </span>
  );
};

/**
 * LinkVerificationModal — connect an already-booked purchase verifikat to an
 * asset registered without one. Candidates (verifikat that debit the asset's
 * tillgångskonto) are listed first; the rest are searchable below.
 */
const LinkVerificationModal = ({ show, onHide, companyId, asset, onLinked }) => {
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !asset) return;
    let cancelled = false;
    (async () => {
      try {
        const year = new Date(asset.acquisition_date).getFullYear();
        const response = await axios.get(
          getApiUrl(`${companyId}/verifications/${year}`),
          { withCredentials: true }
        );
        const verifications = unwrapData(response)?.verifications || [];
        const opts = verifications.map((v) => {
          const debitsAssetAccount = (v.transactions || []).some(
            (t) => t.transaction_type === 'debit' && String(t.account_number) === String(asset.asset_account)
          );
          return {
            value: v.verification_id,
            label: `${v.series || 'V'}${v.number} · ${v.description || ''} · ${formatDate(v.date)}`,
            candidate: debitsAssetAccount,
          };
        }).sort((a, b) => Number(b.candidate) - Number(a.candidate));
        if (!cancelled) setOptions(opts);
      } catch (err) {
        if (!cancelled) setOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [show, asset, companyId]);

  const handleLink = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(
        getApiUrl(`${companyId}/assets/${asset.asset_id}`),
        { source_verification_id: selected.value },
        { withCredentials: true }
      );
      toast.success('Inköpsverifikatet är kopplat till tillgången.');
      onHide();
      if (onLinked) onLinked();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte koppla verifikatet.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered contentClassName="asset-form">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="h5">Koppla inköpsverifikat</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted" style={{ fontSize: '0.875rem' }}>
          Välj verifikatet där köpet bokfördes — då hänger registret och
          bokföringen ihop hela vägen. Verifikat som bokförts på tillgångens
          konto ({asset?.asset_account}) visas först.
        </p>
        {options === null ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : (
          <Select
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder="Sök på nummer eller beskrivning…"
            noOptionsMessage={() => 'Inga verifikat hittades'}
            theme={brandSelectTheme}
            formatOptionLabel={(option) => (
              <span style={{ fontWeight: option.candidate ? 600 : 400 }}>{option.label}</span>
            )}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
        <Button variant="primary" onClick={handleLink} disabled={!selected || saving}>
          {saving ? 'Kopplar…' : 'Koppla'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Muted-label/value pair for the top summary strip.
const StripStat = ({ label, value }) => (
  <div className="asset-strip-stat">
    <div className="asset-strip-label">{label}</div>
    <div className="asset-strip-value asset-num">{value}</div>
  </div>
);

const AssetDetail = () => {
  const { companyId, assetId } = useParams();
  const history = useHistory();

  const [asset, setAsset] = useState(null);
  // events: the posted ledger events; entries: the engine's full plan rows
  // ({ period, amount, booked }) from GET /assets/:id/schedule.
  const [schedule, setSchedule] = useState({ events: [], entries: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showFullPlan, setShowFullPlan] = useState(false);
  const [showDisposal, setShowDisposal] = useState(false);
  const [showWritedown, setShowWritedown] = useState(false);
  const [disposalType, setDisposalType] = useState('sale');
  const [showEdit, setShowEdit] = useState(false);
  const [showLinkVerification, setShowLinkVerification] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', location: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [verificationRow, setVerificationRow] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [accountingUnavailable, setAccountingUnavailable] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccountingUnavailable(null);
    try {
      const [assetRes, scheduleRes] = await Promise.allSettled([
        axios.get(getApiUrl(`${companyId}/assets/${assetId}`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/assets/${assetId}/schedule`), { withCredentials: true }),
      ]);

      if (assetRes.status === 'fulfilled') {
        const data = unwrapData(assetRes.value);
        setAsset(data?.asset || data);
      } else {
        setError('Det gick inte att hämta tillgången. Försök igen senare.');
      }
      if (scheduleRes.status === 'fulfilled') {
        const data = unwrapData(scheduleRes.value) || {};
        setAccountingUnavailable(getAssetAccountingUnavailable(data?.accountingCapability));
        setSchedule({
          events: data.events || data.actual || [],
          // Older payload shapes named the plan rows differently; entries
          // without a `booked` flag simply render as planned.
          entries: data.schedule?.entries || data.projected || data.upcoming || [],
        });
      } else {
        setAccountingUnavailable(getAssetAccountingUnavailable(scheduleRes.reason));
      }
    } catch (err) {
      setError('Det gick inte att hämta tillgången. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId, assetId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const acquisition = parseFloat(asset?.acquisition_value) || 0;
  const residual = parseFloat(asset?.residual_value) || 0;

  const bookValue = useMemo(() => {
    if (!asset) return 0;
    // A disposed asset is fully derecognized — its remaining value left the
    // books via the disposal verifikat, so the strip shows 0.
    if (asset.status === 'disposed') return 0;
    const direct = asset.book_value ?? asset.remaining_value;
    if (direct !== undefined && direct !== null) return round2(direct);
    const acc = parseFloat(asset.accumulated_depreciation) || 0;
    return round2(acquisition - acc);
  }, [asset, acquisition]);

  const accumulatedDepreciation = useMemo(() => {
    const direct = parseFloat(asset?.accumulated_depreciation);
    if (!isNaN(direct)) return round2(direct);
    return round2(acquisition - bookValue);
  }, [asset, acquisition, bookValue]);

  const depreciable = Math.max(acquisition - residual, 0);

  const endDate = useMemo(() => {
    if (!asset || accountingUnavailable) return null;
    const lastEntry = schedule.entries[schedule.entries.length - 1];
    if (lastEntry?.period) return periodToDate(lastEntry.period);
    const start = asset.in_service_date ? new Date(asset.in_service_date) : null;
    const months = parseInt(asset.useful_months, 10);
    if (!start || isNaN(start.getTime()) || !months) return null;
    return addMonths(new Date(start.getFullYear(), start.getMonth(), 1), months - 1);
  }, [asset, schedule.entries, accountingUnavailable]);

  const monthlyAmount = useMemo(() => {
    if (accountingUnavailable) return null;
    if (asset?.monthly_amount) return round2(asset.monthly_amount);
    const next = schedule.entries.find((row) => !row.booked && parseFloat(row.amount) > 0);
    if (next) return round2(next.amount);
    const months = parseInt(asset?.useful_months, 10);
    return months ? round2(depreciable / months) : 0;
  }, [asset, schedule.entries, depreciable, accountingUnavailable]);

  // Posted depreciation events by period — links a booked ledger row to its
  // verifikat. (Multiple events per period shouldn't happen; last one wins.)
  const depEventByPeriod = useMemo(() => {
    const map = {};
    (schedule.events || [])
      .filter((e) => e.event_type === 'depreciation' && e.period)
      .forEach((e) => { map[e.period] = e; });
    return map;
  }, [schedule.events]);

  // The unified plan ledger: one row per period, booked rows first-class.
  // Periods more than 12 months out collapse behind "Visa hela planen".
  const currentPeriod = dateToPeriod(new Date());
  const collapseAfter = dateToPeriod(addMonths(new Date(), 12));
  const ledger = useMemo(() => {
    // A disposed asset has no future: planned rows would show a schedule
    // that will never post. Keep only what was actually booked.
    const isDisposed = asset?.status === 'disposed';
    const rows = (schedule.entries || [])
      .map((entry) => ({
        period: entry.period,
        amount: parseFloat(entry.amount) || 0,
        booked: Boolean(entry.booked),
        event: entry.period ? depEventByPeriod[entry.period] : undefined,
      }))
      .filter((row) => !isDisposed || row.booked);
    const farAheadCount = rows.filter((row) => row.period && row.period > collapseAfter).length;
    const visible = showFullPlan
      ? rows
      : rows.filter((row) => !row.period || row.period <= collapseAfter);
    return { rows, visible, farAheadCount };
  }, [asset?.status, schedule.entries, depEventByPeriod, showFullPlan, collapseAfter]);

  // Chart series: start at acquisition value, decline through posted
  // depreciation (solid) and the remaining plan (dashed).
  const chartPoints = useMemo(() => {
    if (!asset) return [];
    const start = asset.in_service_date ? new Date(asset.in_service_date) : new Date();
    const points = [{ date: new Date(start.getFullYear(), start.getMonth(), 1), value: acquisition, projected: false }];
    let running = acquisition;

    const decliningEvents = (schedule.events || [])
      .filter((e) => ['depreciation', 'opening_balance', 'writedown'].includes(e.event_type))
      .sort((a, b) => String(a.period || a.event_date).localeCompare(String(b.period || b.event_date)));

    decliningEvents.forEach((event) => {
      running = round2(running - Math.abs(parseFloat(event.amount) || 0));
      points.push({
        date: periodToDate(event.period) || new Date(event.event_date),
        value: Math.max(running, 0),
        projected: false,
      });
    });

    // Disposed: the story ends at derecognition — a final drop to 0 at the
    // disposal date, no dashed future.
    if (asset.status === 'disposed') {
      const disposal = (schedule.events || []).find((e) =>
        ['disposal_sale', 'disposal_scrap'].includes(e.event_type));
      points.push({
        date: disposal ? new Date(disposal.event_date) : new Date(),
        value: 0,
        projected: false,
      });
      return points;
    }

    (schedule.entries || []).filter((row) => !row.booked).forEach((row) => {
      running = round2(running - Math.abs(parseFloat(row.amount) || 0));
      points.push({
        date: periodToDate(row.period) || new Date(),
        value: Math.max(running, 0),
        projected: true,
      });
    });

    return points;
  }, [asset, acquisition, schedule]);

  // Händelser: the non-depreciation ledger events (anskaffning, nedskrivning,
  // avveckling …) — the monthly depreciation rows live in the plan ledger.
  const timelineEvents = useMemo(
    () => (schedule.events || [])
      .filter((e) => e.event_type !== 'depreciation')
      .sort((a, b) => String(b.event_date || b.period).localeCompare(String(a.event_date || a.period))),
    [schedule.events]
  );

  const handleShowVerification = async (verificationId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/verification/${verificationId}`),
        { withCredentials: true }
      );
      const verification = response.data;
      let debitSum = 0;
      let creditSum = 0;
      (verification.transactions || []).forEach((t) => {
        const amount = parseFloat(t.amount) || 0;
        if (t.transaction_type === 'debit') debitSum += amount;
        else creditSum += amount;
      });
      setVerificationRow({ ...verification, debitSum: round2(debitSum), creditSum: round2(creditSum) });
      setShowVerificationDetails(true);
    } catch (err) {
      toast.error('Kunde inte hämta verifikatet.');
    }
  };

  const openEdit = () => {
    setEditForm({
      name: asset?.name || '',
      location: asset?.location || '',
      notes: asset?.notes || '',
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      await axios.put(
        getApiUrl(`${companyId}/assets/${assetId}`),
        { name: editForm.name.trim(), location: editForm.location, notes: editForm.notes },
        { withCredentials: true }
      );
      toast.success('Tillgången är uppdaterad.');
      setShowEdit(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte spara ändringarna.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Laddar tillgång…</p>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="pt-3">
        <Alert variant="danger">{error || 'Tillgången hittades inte.'}</Alert>
        <Button variant="outline-secondary" onClick={() => history.push(`/${companyId}/assets`)}>
          Tillbaka till Tillgångar
        </Button>
      </div>
    );
  }

  const category = getCategoryMeta(asset.category_code || asset.category);
  const isDisposed = asset.status === 'disposed';
  const canDispose = asset.status === 'active' || asset.status === 'fully_depreciated';
  // Mark (is_depreciable 0): no plan, no chart, no nedskrivning via modulen.
  const isNonDepreciable = Number(asset.is_depreciable) === 0;
  // K1 (BAS 2018 K1): pool assets have no per-asset book value, plan or
  // writedown — the collective valuation lives in the year-end panel
  // (docs/bas-2026/K1_ASSET_ACCOUNTING_SPEC.md §3). Property assets keep
  // their accumulated figures (1119/1159 are genuine accumulated accounts).
  const isK1Pool = asset.accounting_model === 'k1_collective_pool';
  const isK1 = String(asset.accounting_model || '').startsWith('k1');
  const canWritedown = asset.status === 'active' && !isK1 && Boolean(asset.writedown_contra_account);

  return (
    <div className="pt-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => history.push(`/${companyId}/assets`)}
          >
            <ArrowLeft size={14} /> Tillgångar
          </Button>
          <div className="d-flex align-items-center gap-2">
            <CategoryChip code={category.code} size={40} iconSize={19} />
            <div>
              <h4 style={{ fontWeight: 700, color: 'var(--title)', marginBottom: 0 }}>
                {asset.name}
                {asset.asset_number && (
                  <span style={{ fontSize: '0.875rem', color: 'var(--secondary)', fontWeight: 500, marginLeft: 8 }}>
                    #{asset.asset_number}
                  </span>
                )}
              </h4>
              <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>{category.label}</div>
            </div>
          </div>
          <StatusPill status={asset.status} size="md" />
        </div>
        <div className="d-flex align-items-center gap-2">
          {!accountingUnavailable && canDispose && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => { setDisposalType('sale'); setShowDisposal(true); }}
              >
                <Banknote size={14} /> Sälj
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => { setDisposalType('scrap'); setShowDisposal(true); }}
              >
                <Trash2 size={14} /> Skrota
              </Button>
              {canWritedown && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={() => setShowWritedown(true)}
                >
                  <TrendingDown size={14} /> Skriv ned
                </Button>
              )}
            </>
          )}
          {!accountingUnavailable && <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={openEdit}
          >
            <Pencil size={14} /> Redigera
          </Button>}
        </div>
      </div>

      {accountingUnavailable && (
        <Alert variant="warning">
          <Alert.Heading>{accountingUnavailable.heading}</Alert.Heading>
          <p className="mb-1">{accountingUnavailable.message}</p>
          <p className="mb-0">
            Sparade registeruppgifter och bokförda händelser visas nedan. Prognoser och alla
            ändringar är avstängda tills rätt redovisningsmodell finns.
          </p>
        </Alert>
      )}

      {/* Schedule details panel: summary strip → balance chart → plan ledger */}
      <div className="modern-card">
        <div style={{ padding: '1.5rem 2rem 1.25rem' }}>
          {/* Summary strip — muted-label/value pairs. K1 pool assets show
              only register facts (the valuation is collective, p. 6.38). */}
          <div className="asset-strip">
            <StripStat label="Anskaffningsvärde" value={`${formatNumber(acquisition, 0)} kr`} />
            {isK1Pool ? (
              <>
                <StripStat label="Värdering" value="Kollektiv (K1)" />
                <StripStat
                  label="Användningstid"
                  value={asset.useful_months ? `${Math.round(asset.useful_months / 12)} år` : '—'}
                />
                <StripStat
                  label="Status"
                  value={isDisposed
                    ? `Avvecklad${asset.disposed_at ? ` ${formatMonthYear(asset.disposed_at)}` : ''}`
                    : 'I förteckningen'}
                />
              </>
            ) : (
              <>
                <StripStat label="Ack. avskrivningar" value={`${formatNumber(accumulatedDepreciation, 0)} kr`} />
                <StripStat label="Bokfört värde" value={`${formatNumber(bookValue, 0)} kr`} />
                <StripStat
                  label="Skrivs av t.o.m."
                  value={isDisposed
                    ? `Avvecklad${asset.disposed_at ? ` ${formatMonthYear(asset.disposed_at)}` : ''}`
                    : endDate ? formatMonthYear(endDate) : '—'}
                />
              </>
            )}
          </div>

          {!accountingUnavailable && !isDisposed && monthlyAmount > 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginBottom: '1rem' }}>
              <strong style={{ color: 'var(--title)' }}>{formatNumber(monthlyAmount, 0)} kr/mån</strong>
              {' '}· linjär avskrivning, bokförs den siste varje månad.
            </div>
          )}
          {isDisposed && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              Tillgången är avvecklad{asset.disposed_at ? ` (${formatDate(asset.disposed_at)})` : ''}.
            </p>
          )}
          {isNonDepreciable && !isDisposed && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              Mark skrivs inte av — värdet ligger kvar i balansräkningen tills den säljs.
            </p>
          )}
          {isK1Pool && !isDisposed && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              Tillgången värderas kollektivt tillsammans med företagets övriga maskiner
              och inventarier vid bokslutet (förenklat årsbokslut) — se fliken
              Årsvärdering (K1) under Tillgångar.
            </p>
          )}

          {!accountingUnavailable && !isNonDepreciable && !isK1Pool && <BalanceDeclineChart points={chartPoints} />}

          <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
            <Button
              variant="link"
              size="sm"
              className="d-flex align-items-center gap-1"
              style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
              onClick={() => setShowDetails(true)}
            >
              <Info size={14} /> Visa detaljer
            </Button>
            {asset.supplier_invoice_id && (
              <Button
                variant="link"
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{ textDecoration: 'none', color: 'var(--primary)' }}
                onClick={() => history.push(`/${companyId}/supplier-invoices/${asset.supplier_invoice_id}`)}
              >
                <Receipt size={14} /> Visa leverantörsfakturan
              </Button>
            )}
            {asset.source_verification_id ? (
              <Button
                variant="link"
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{ textDecoration: 'none', color: 'var(--primary)' }}
                onClick={() => handleShowVerification(asset.source_verification_id)}
              >
                <FileText size={14} /> Inköpsverifikat
              </Button>
            ) : !accountingUnavailable && asset.status !== 'disposed' && (
              <Button
                variant="link"
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{ textDecoration: 'none', color: 'var(--secondary)' }}
                onClick={() => setShowLinkVerification(true)}
              >
                <FileText size={14} /> Koppla inköpsverifikat
              </Button>
            )}
          </div>
        </div>

        {/* Plan ledger — one row per period, booked and planned unified. */}
        {ledger.rows.length > 0 && (
          <div style={{ borderTop: '1px solid #e5e7eb' }}>
            <Table className="mb-0 align-middle asset-ledger-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 32 }}>Period</th>
                  <th className="text-end">Belopp</th>
                  <th style={{ paddingLeft: 24 }}>Status</th>
                  <th style={{ paddingRight: 32 }} />
                </tr>
              </thead>
              <tbody>
                {ledger.visible.map((row) => {
                  const isCurrent = row.period === currentPeriod;
                  const isFinal = row.period === ledger.rows[ledger.rows.length - 1]?.period;
                  return (
                    <tr key={row.period} className={isCurrent ? 'asset-ledger-current' : undefined}>
                      <td style={{ paddingLeft: 32, fontWeight: isCurrent ? 700 : 500 }}>
                        {formatMonthYear(row.period)}
                        {isCurrent && <span className="asset-ledger-now">Denna månad</span>}
                        {isFinal && (
                          <span
                            className="d-inline-flex align-items-center gap-1"
                            style={{ marginLeft: 10, fontSize: '0.6875rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                          >
                            <Flag size={12} strokeWidth={2} /> Helt avskriven
                          </span>
                        )}
                      </td>
                      <td className="text-end asset-num" style={{ fontWeight: 600 }}>
                        {formatNumber(row.amount)} kr
                      </td>
                      <td style={{ paddingLeft: 24 }}>
                        {row.booked ? (
                          <span className="asset-ledger-pill booked">Bokförd</span>
                        ) : (
                          <span className="asset-ledger-pill planned">Planerad</span>
                        )}
                      </td>
                      <td className="text-end" style={{ paddingRight: 32 }}>
                        {row.event?.verification_id && (
                          <Button
                            variant="link"
                            size="sm"
                            className="d-inline-flex align-items-center gap-1"
                            style={{ textDecoration: 'none', color: 'var(--primary)', padding: 0 }}
                            onClick={() => handleShowVerification(row.event.verification_id)}
                          >
                            <FileText size={14} /> Verifikat
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {ledger.farAheadCount > 0 && (
              <button
                type="button"
                className="asset-ledger-expander"
                onClick={() => setShowFullPlan((prev) => !prev)}
              >
                {showFullPlan
                  ? (<><ChevronUp size={14} /> Visa färre</>)
                  : (<><ChevronDown size={14} /> Visa hela planen ({ledger.farAheadCount} månader till)</>)}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Events timeline — non-depreciation ledger events. */}
      <div className="modern-card">
        <div className="modern-card-header">
          <h5 className="mb-0" style={{ fontWeight: 600 }}>Händelser</h5>
        </div>
        <div style={{ padding: '1.25rem 2rem' }}>
          {timelineEvents.length === 0 ? (
            <p className="text-muted mb-0">
              Inga händelser ännu — anskaffning, nedskrivningar och avvecklingar visas här.
            </p>
          ) : (
            <div>
              {timelineEvents.map((event, index) => (
                <div
                  key={event.event_id || index}
                  className="d-flex align-items-center gap-3 flex-wrap"
                  style={{
                    padding: '0.65rem 0',
                    borderBottom: index < timelineEvents.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}
                >
                  <EventChip eventType={event.event_type} hasVerification={!!event.verification_id} />
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <span style={{ fontWeight: 600, color: 'var(--title)', fontSize: '0.875rem' }}>
                      {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                    </span>
                    {event.memo && event.memo !== (EVENT_TYPE_LABELS[event.event_type] || event.event_type) && (
                      <span style={{ color: 'var(--secondary)', fontSize: '0.8125rem', marginLeft: 8 }}>
                        {event.memo}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                    {formatDate(event.event_date)}
                  </span>
                  <span className="asset-num" style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--title)', minWidth: 90, textAlign: 'right' }}>
                    {formatNumber(event.amount)} kr
                  </span>
                  {event.verification_id ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="d-flex align-items-center gap-1"
                      style={{ textDecoration: 'none', color: 'var(--primary)', padding: 0 }}
                      onClick={() => handleShowVerification(event.verification_id)}
                    >
                      <FileText size={14} /> Verifikat
                    </Button>
                  ) : (
                    <span style={{ width: 76 }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Details modal. NOT Offcanvas: the app's bundled theme CSS predates
          Bootstrap 5's offcanvas module, so an Offcanvas renders unstyled
          below the viewport while still locking body scroll — a trap. Modal
          is the app-wide pattern and fully styled. */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} centered contentClassName="asset-form">
        <Modal.Header closeButton>
          <Modal.Title as="h5">Detaljer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <dl style={{ fontSize: '0.875rem' }}>
            <dt className="text-muted">Anskaffningsvärde</dt>
            <dd style={{ fontWeight: 600 }}>{formatNumber(acquisition)} kr</dd>
            <dt className="text-muted">Anskaffningsdatum</dt>
            <dd style={{ fontWeight: 600 }}>{formatDate(asset.acquisition_date)}</dd>
            <dt className="text-muted">Togs i bruk</dt>
            <dd style={{ fontWeight: 600 }}>{formatDate(asset.in_service_date)}</dd>
            <dt className="text-muted">Nyttjandeperiod</dt>
            <dd style={{ fontWeight: 600 }}>
              {asset.useful_months ? `${Math.round(asset.useful_months / 12)} år (${asset.useful_months} mån)` : '—'}
            </dd>
            <dt className="text-muted">Restvärde</dt>
            <dd style={{ fontWeight: 600 }}>{formatNumber(residual)} kr</dd>
            <dt className="text-muted">Bokfört värde</dt>
            <dd style={{ fontWeight: 600 }}>{formatNumber(bookValue)} kr</dd>
            {(asset.asset_account || asset.accounts) && (
              <>
                <dt className="text-muted">Konton</dt>
                <dd style={{ fontWeight: 600 }}>
                  {asset.asset_account && <div>Tillgång: {asset.asset_account}</div>}
                  {asset.accdep_account && <div>Ack. avskrivningar: {asset.accdep_account}</div>}
                  {asset.depexp_account && <div>Avskrivningskostnad: {asset.depexp_account}</div>}
                </dd>
              </>
            )}
            {asset.serial_number && (
              <>
                <dt className="text-muted">Serienummer</dt>
                <dd style={{ fontWeight: 600 }}>{asset.serial_number}</dd>
              </>
            )}
            {asset.location && (
              <>
                <dt className="text-muted">Plats</dt>
                <dd style={{ fontWeight: 600 }}>{asset.location}</dd>
              </>
            )}
            {asset.notes && (
              <>
                <dt className="text-muted">Anteckningar</dt>
                <dd>{asset.notes}</dd>
              </>
            )}
          </dl>
        </Modal.Body>
      </Modal>

      {/* Edit modal — name/location/notes only (everything else is ledger-backed) */}
      <Modal show={!accountingUnavailable && showEdit} onHide={saving ? undefined : () => setShowEdit(false)} centered contentClassName="asset-form">
        <Modal.Header closeButton={!saving}>
          <Modal.Title as="h5">Redigera tillgång</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="edit-name">
            <Form.Label>Namn</Form.Label>
            <Form.Control
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="edit-location">
            <Form.Label>Plats</Form.Label>
            <Form.Control
              type="text"
              value={editForm.location}
              onChange={(e) => setEditForm((prev) => ({ ...prev, location: e.target.value }))}
              placeholder="T.ex. kontoret, bilen, lagret"
            />
          </Form.Group>
          <Form.Group className="mb-1" controlId="edit-notes">
            <Form.Label>Anteckningar</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </Form.Group>
          <Form.Text muted>
            Belopp, datum och nyttjandeperiod styr bokföringen och kan inte ändras här.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEdit(false)} disabled={saving}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saving || !editForm.name.trim()}>
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
        </Modal.Footer>
        <style>{ASSET_FORM_STYLES}</style>
      </Modal>

      <DisposalModal
        show={!accountingUnavailable && showDisposal}
        onHide={() => setShowDisposal(false)}
        companyId={companyId}
        asset={asset}
        initialType={disposalType}
        onDone={fetchData}
      />

      <WritedownModal
        show={!accountingUnavailable && showWritedown}
        onHide={() => setShowWritedown(false)}
        companyId={companyId}
        asset={asset}
        onDone={fetchData}
      />

      <LinkVerificationModal
        show={!accountingUnavailable && showLinkVerification}
        onHide={() => setShowLinkVerification(false)}
        companyId={companyId}
        asset={asset}
        onLinked={fetchData}
      />

      <VerificationDetailsModal
        showVerificationDetails={showVerificationDetails}
        setShowVerificationDetails={setShowVerificationDetails}
        selectedRow={verificationRow}
        viewOnly
      />

      <style>{`
        .asset-num { font-variant-numeric: tabular-nums; }

        /* Summary strip. */
        .asset-strip { display: flex; flex-wrap: wrap; gap: 2rem 3rem; padding-bottom: 1rem; margin-bottom: 1rem; border-bottom: 1px solid #f3f4f6; }
        .asset-strip-stat { min-width: 130px; }
        .asset-strip-label { font-size: 0.6875rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
        .asset-strip-value { font-size: 1.05rem; font-weight: 700; color: #111827; white-space: nowrap; }

        /* Plan ledger. */
        .asset-ledger-table thead th {
          background: #f9fafb;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        .asset-ledger-table tbody td { border-color: #f3f4f6; font-size: 0.875rem; }
        .asset-ledger-current > td { background: var(--rgba-primary-1); }
        .asset-ledger-now {
          margin-left: 8px;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--primary);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .asset-ledger-pill { border-radius: 999px; padding: 2px 10px; font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
        .asset-ledger-pill.booked { background: #dcfce7; color: #166534; }
        .asset-ledger-pill.planned { background: #f1f5f9; color: #475569; }
        .asset-ledger-expander {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          border: none;
          border-top: 1px solid #f3f4f6;
          background: #fff;
          color: var(--primary);
          font-size: 0.8125rem;
          font-weight: 600;
          padding: 10px 16px;
          cursor: pointer;
        }
        .asset-ledger-expander:hover { background: #f9fafb; }
      `}</style>
    </div>
  );
};

export default AssetDetail;
